import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  Plus, Trash2, Loader2, User, Star, TrendingUp, TrendingDown,
  ArrowDownLeft, ArrowUpRight, Search, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const formatBRL = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

// ==================== INVESTOR PROFILE ====================
export const InvestorProfilePanel = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState({ cpf: '', cnpj: '', investor_name: '', broker: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/br/investor-profile`, { withCredentials: true })
      .then(r => setProfile(p => ({ ...p, ...r.data })))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/br/investor-profile`, profile, { withCredentials: true });
      toast.success(t('br.profile.saved'));
    } catch { toast.error(t('br.profile.saveFailed')); }
    finally { setSaving(false); }
  };

  const formatCPF = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  };

  const formatCNPJ = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
    return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
  };

  return (
    <Card className="bg-[#141414] border-white/5">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <User className="h-4 w-4 text-sky-400" />
          <span className="text-white font-medium text-sm">{t('br.profile.title')}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-400 text-xs">CPF</Label>
            <Input value={profile.cpf} onChange={e => setProfile({...profile, cpf: formatCPF(e.target.value)})}
              placeholder="000.000.000-00" className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-10 font-mono" data-testid="br-profile-cpf" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">CNPJ</Label>
            <Input value={profile.cnpj} onChange={e => setProfile({...profile, cnpj: formatCNPJ(e.target.value)})}
              placeholder="00.000.000/0000-00" className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-10 font-mono" data-testid="br-profile-cnpj" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">{t('br.profile.name')}</Label>
            <Input value={profile.investor_name} onChange={e => setProfile({...profile, investor_name: e.target.value})}
              className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-10" data-testid="br-profile-name" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">{t('br.profile.broker')}</Label>
            <Input value={profile.broker} onChange={e => setProfile({...profile, broker: e.target.value})}
              placeholder="XP, Rico, NuInvest..." className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-10" data-testid="br-profile-broker" />
          </div>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving} className="bg-sky-600 text-white hover:bg-sky-500 rounded-lg" data-testid="br-profile-save">
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}{t('common.save')}
        </Button>
      </CardContent>
    </Card>
  );
};

// ==================== PORTFOLIO ANALYSIS CHARTS ====================
export const PortfolioCharts = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/br/portfolio-analysis`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  if (!data || (!data.by_type?.length && !data.by_sector?.length)) return <p className="text-zinc-600 text-sm text-center py-8">{t('br.noHoldings')}</p>;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-xs">
        <p className="text-white font-medium">{payload[0].name || payload[0].payload?.name}</p>
        <p className="text-emerald-400 font-mono">{formatBRL(payload[0].value)}</p>
      </div>
    );
  };

  const renderLabel = ({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : '';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* By Type */}
      <Card className="bg-[#141414] border-white/5">
        <CardContent className="p-4">
          <p className="text-zinc-400 text-xs font-medium mb-3">{t('br.charts.byType')}</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.by_type} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85}
                label={renderLabel} labelLine={false} stroke="none">
                {data.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      {/* By Sector */}
      <Card className="bg-[#141414] border-white/5">
        <CardContent className="p-4">
          <p className="text-zinc-400 text-xs font-medium mb-3">{t('br.charts.bySector')}</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.by_sector} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85}
                label={renderLabel} labelLine={false} stroke="none">
                {data.by_sector.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== DIVIDEND MANAGEMENT ====================
export const DividendPanel = () => {
  const { t } = useTranslation();
  const [divs, setDivs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ ticker: '', amount: '', date: new Date().toISOString().split('T')[0], type: 'dividend', notes: '' });
  const [showForm, setShowForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const fetchDivs = useCallback(async () => {
    try {
      const [divRes, sumRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/br/dividends`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/br/dividend-summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/br/sync-dividends-status`, { withCredentials: true }),
      ]);
      setDivs(divRes.data || []);
      setSummary(sumRes.data);
      setSyncStatus(statusRes.data);
    } catch {}
  }, []);

  useEffect(() => { fetchDivs(); }, [fetchDivs]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/br/sync-dividends`, {}, { withCredentials: true });
      toast.success(`${t('br.dividend.synced')}: ${res.data.synced} ${t('br.dividend.newRecords')}`);
      fetchDivs();
    } catch { toast.error(t('br.dividend.syncFailed')); }
    finally { setSyncing(false); }
  };

  const handleAdd = async () => {
    if (!form.ticker || !form.amount) { toast.error(t('br.fillRequired')); return; }
    try {
      await axios.post(`${API_URL}/api/admin/br/dividends`, {
        ...form, ticker: form.ticker.toUpperCase(), amount: parseFloat(form.amount),
      }, { withCredentials: true });
      toast.success(t('br.dividend.added'));
      setForm({ ticker: '', amount: '', date: new Date().toISOString().split('T')[0], type: 'dividend', notes: '' });
      setShowForm(false);
      fetchDivs();
    } catch { toast.error(t('br.addFailed')); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/br/dividends/${id}`, { withCredentials: true });
      toast.success(t('br.deleted'));
      fetchDivs();
    } catch {}
  };

  const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-xs">
        <p className="text-zinc-400">{payload[0].payload?.month}</p>
        <p className="text-emerald-400 font-mono font-bold">{formatBRL(payload[0].value)}</p>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Monthly chart */}
      {summary?.monthly?.length > 0 && (
        <Card className="bg-[#141414] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-zinc-400 text-xs font-medium">{t('br.dividend.monthlyChart')}</p>
              <p className="text-emerald-400 text-sm font-mono font-bold">{t('br.total')}: {formatBRL(summary.total)}</p>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={summary.monthly} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="month" tick={{ fill: '#666', fontSize: 10 }} />
                <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="amount" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Sync + Add + List */}
      <Card className="bg-[#141414] border-white/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <p className="text-white font-medium text-sm">{t('br.dividend.title')}</p>
              {syncStatus?.last_sync && (
                <span className="text-zinc-600 text-[10px]">
                  {t('br.dividend.lastSync')}: {new Date(syncStatus.last_sync).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSync} disabled={syncing}
                className="bg-sky-600 text-white hover:bg-sky-500 rounded-lg h-7 text-xs" data-testid="br-div-sync">
                {syncing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                B3 {t('br.dividend.autoSync')}
              </Button>
              <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-emerald-600 text-white hover:bg-emerald-500 rounded-lg h-7 text-xs" data-testid="br-div-add-btn">
                <Plus className="h-3 w-3 mr-1" />{t('br.dividend.add')}
              </Button>
            </div>
          </div>

          {showForm && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4 p-3 bg-[#0A0A0A] rounded-lg border border-white/5">
              <Input value={form.ticker} onChange={e => setForm({...form, ticker: e.target.value})} placeholder="MXRF11"
                className="bg-transparent border-white/10 text-white rounded-lg h-8 text-xs" data-testid="br-div-ticker" />
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="R$ 0,00"
                className="bg-transparent border-white/10 text-white rounded-lg h-8 text-xs" data-testid="br-div-amount" />
              <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}
                className="bg-transparent border-white/10 text-white rounded-lg h-8 text-xs [color-scheme:dark]" data-testid="br-div-date" />
              <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                <SelectTrigger className="bg-transparent border-white/10 text-white rounded-lg h-8 text-xs" data-testid="br-div-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="dividend" className="text-zinc-300">Dividendo</SelectItem>
                  <SelectItem value="jcp" className="text-zinc-300">JCP</SelectItem>
                  <SelectItem value="rendimento" className="text-zinc-300">Rendimento</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleAdd} className="bg-emerald-600 text-white hover:bg-emerald-500 rounded-lg h-8 text-xs" data-testid="br-div-submit">
                <Plus className="h-3 w-3 mr-1" />{t('common.save')}
              </Button>
            </div>
          )}

          {divs.length === 0 ? (
            <p className="text-zinc-600 text-sm text-center py-4">{t('br.dividend.none')}</p>
          ) : (
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-xs" data-testid="br-dividend-table">
                <thead><tr className="border-b border-white/5 text-zinc-500">
                  <th className="text-left py-2 px-2">{t('br.date')}</th>
                  <th className="text-left py-2 px-2">{t('br.ticker')}</th>
                  <th className="text-left py-2 px-2">{t('br.txType')}</th>
                  <th className="text-right py-2 px-2">{t('br.dividend.perUnit')}</th>
                  <th className="text-right py-2 px-2">{t('br.quantity')}</th>
                  <th className="text-right py-2 px-2">{t('br.amount')}</th>
                  <th className="text-center py-2 px-2">{t('br.source')}</th>
                  <th className="text-center py-2 px-2"></th>
                </tr></thead>
                <tbody>
                  {divs.map(d => (
                    <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-1.5 px-2 text-zinc-400 font-mono">{d.date}</td>
                      <td className="py-1.5 px-2 text-white font-mono font-semibold">{d.ticker}</td>
                      <td className="py-1.5 px-2"><span className="bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded text-[10px]">{d.type}</span></td>
                      <td className="py-1.5 px-2 text-right text-zinc-400 font-mono">{d.amount_per_unit ? `R$ ${d.amount_per_unit.toFixed(4)}` : '-'}</td>
                      <td className="py-1.5 px-2 text-right text-zinc-400 font-mono">{d.quantity || '-'}</td>
                      <td className="py-1.5 px-2 text-right text-emerald-400 font-mono font-medium">{formatBRL(d.amount)}</td>
                      <td className="py-1.5 px-2 text-center">
                        {d.source === 'b3_auto' ? (
                          <span className="bg-sky-500/20 text-sky-400 px-1.5 py-0.5 rounded text-[10px]">B3</span>
                        ) : (
                          <span className="bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded text-[10px]">{t('br.dividend.manual')}</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <button onClick={() => handleDelete(d.id)} className="text-zinc-600 hover:text-red-400" data-testid={`br-div-del-${d.id}`}><Trash2 className="h-3 w-3" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== HIGH DIVIDEND SCREENING ====================
export const ScreeningPanel = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/br/screening`, { withCredentials: true })
      .then(r => setData(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;

  const filtered = filter
    ? data.filter(d => d.ticker.toLowerCase().includes(filter.toLowerCase()))
    : data;

  return (
    <Card className="bg-[#141414] border-white/5">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-amber-400" />
            <span className="text-white font-medium text-sm">{t('br.screening.title')}</span>
            <span className="text-zinc-600 text-xs">({filtered.length})</span>
          </div>
          <div className="relative w-48">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-500" />
            <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('br.screening.filter')}
              className="bg-[#0A0A0A] border-white/10 text-white rounded-lg h-7 text-xs pl-7" data-testid="br-screen-filter" />
          </div>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="br-screening-table">
            <thead className="sticky top-0 bg-[#141414]">
              <tr className="border-b border-white/5 text-zinc-500">
                <th className="text-left py-2 px-2">{t('br.ticker')}</th>
                <th className="text-right py-2 px-2">{t('br.currentPrice')}</th>
                <th className="text-right py-2 px-2">{t('br.change')}</th>
                <th className="text-right py-2 px-2"><span className="text-amber-400">Yield</span></th>
                <th className="text-right py-2 px-2">{t('br.monthlyDiv')}</th>
                <th className="text-center py-2 px-2">P/VP</th>
                <th className="text-center py-2 px-2">{t('br.screening.held')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.ticker} className={`border-b border-white/5 hover:bg-white/[0.02] ${s.held ? 'bg-emerald-500/[0.03]' : ''}`}>
                  <td className="py-1.5 px-2 text-white font-mono font-semibold">{s.ticker}</td>
                  <td className="py-1.5 px-2 text-right text-zinc-300 font-mono">{formatBRL(s.price)}</td>
                  <td className="py-1.5 px-2 text-right">
                    <span className={`font-mono ${s.change_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {s.change_pct >= 0 ? '+' : ''}{s.change_pct?.toFixed(2)}%
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <span className={`font-mono font-bold ${s.annual_yield >= 10 ? 'text-emerald-400' : s.annual_yield >= 7 ? 'text-emerald-300' : s.annual_yield >= 4 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {s.annual_yield?.toFixed(2)}%
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-right text-emerald-300 font-mono">{formatBRL(s.monthly_div)}</td>
                  <td className="py-1.5 px-2 text-center font-mono text-xs">
                    {s.p_vp ? (
                      <span className={s.p_vp < 1 ? 'text-emerald-400 font-bold' : s.p_vp > 1.2 ? 'text-red-400' : 'text-zinc-300'}>{s.p_vp}</span>
                    ) : '-'}
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    {s.held && <span className="bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded text-[10px]">{t('br.screening.owned')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
